/**
 * DevicesPage — paired devices, capability status, and signed-dispatch UX.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05C. REQUIRED PROPERTY ("No
 * remote execution without signed envelope and approval"): the dispatch form
 * below is disabled until BOTH an approved approval id AND a reference to an
 * already-signed envelope are filled in. That disable is a UX guard, not the
 * enforcement — the backend bridge (`auteur_cloud/bridge/**`) verifies the
 * signature and the approval server-side and enforces the gate regardless of
 * what any page sends. This page never signs anything itself: signing keys
 * never enter the browser surface.
 *
 * Capability status renders the server-reported `health` per capability
 * verbatim; a missing or stale advertisement shows as the string the server
 * gave, never as an invented "ok".
 *
 * The device list and capability endpoints are mounted. A failed request is
 * shown as an error; the page never substitutes a fabricated device list.
 *
 * BUILD STATUS: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'wouter';

import {
  fetchDeviceCapabilities,
  fetchDevices,
  requestRemoteDispatch,
} from '../../api/operations';
import { issuePairingHandoff, pairingHandoffStatus, issuePairingToken } from '../../api/activation';

export function PairLocalNodePage() {
  const [label, setLabel] = useState('');
  const [handoff, setHandoff] = useState(null);
  const [manualGrant, setManualGrant] = useState(null);
  const [manualNotice, setManualNotice] = useState('');
  const [expiresAt, setExpiresAt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!handoff) return undefined;
    let cancelled = false;
    let timer;
    async function poll() {
      if (cancelled) return;
      if (Date.now() >= expiresAt) {
        setHandoff(null);
        setMessage('This pairing link expired. Select Pair this computer to try again.');
        return;
      }
      try {
        const result = await pairingHandoffStatus(handoff.handoff_code);
        if (cancelled) return;
        if (result.state === 'paired' && result.device_id) {
          const inventory = await fetchDevices();
          if (cancelled) return;
          // Correlate the exact server-bound device ID, never a label or the
          // first/newest device in a list that another session may change.
          const device = inventory.devices.find((item) => item.device_id === result.device_id);
          if (device && device.status !== 'revoked' && (device.online === true || device.connection_state === 'connected')) {
            setMessage('Connected. This computer is ready to use.');
            setHandoff(null);
            return;
          }
          setMessage('Paired. Waiting for this computer to connect.');
        }
      } catch {
        if (!cancelled) setError('Pairing status could not be checked. Check your connection or issue a new link.');
      }
      if (!cancelled) timer = setTimeout(() => void poll(), 3000);
    }
    void poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [handoff, expiresAt]);

  async function issueManualCode() {
    setBusy(true); setError(''); setManualGrant(null); setManualNotice('');
    try { setManualGrant(await issuePairingToken(label.trim())); }
    catch { setError('A pairing code could not be issued. Check your permission and try again.'); }
    finally { setBusy(false); }
  }
  async function copyManualCode() {
    try {
      await navigator.clipboard.writeText(manualGrant.token);
      setManualNotice('Copied. Paste only into the Bridge hidden pairing prompt, never a chat.');
    } catch { setError('Copy is unavailable in this browser. Try pairing from a supported secure browser.'); }
  }

  async function pair(event) {
    event.preventDefault();
    setBusy(true); setError(''); setHandoff(null); setMessage('');
    try {
      const next = await issuePairingHandoff(label.trim());
      setExpiresAt(Date.now() + next.expires_in * 1000);
      setHandoff(next);
      setMessage('Waiting for Auteur Bridge. Confirm pairing in the window on this computer.');
      window.location.assign(next.deep_link);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Pairing could not be started.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="pairing-page">
      <header className="page-heading">
        <p className="eyebrow">Local Node</p><h1>Connect this computer</h1>
        <p>Install Auteur Bridge, then pair it with your signed-in account. Your private device keys stay on this computer.</p>
      </header>
      <p><a href="/settings">Download Auteur Bridge</a> if it is not installed yet.</p>
      {error ? <p role="alert" className="error">{error}</p> : null}
      <form onSubmit={(event) => void pair(event)}>
        <label htmlFor="pair-node-label">Name this computer</label>
        <input id="pair-node-label" maxLength={80} value={label} onChange={(event) => setLabel(event.target.value)} />
        <button disabled={busy || !label.trim()}>{busy ? 'Starting pairing…' : 'Pair this computer'}</button>
      </form>
      {message ? <p role="status">{message}</p> : null}
      {handoff ? <p><a href={handoff.deep_link}>Open Auteur Bridge</a> if your browser did not open it. This link expires {new Date(expiresAt).toLocaleString()}.</p> : null}
      <details>
        <summary>Use a pairing code instead</summary>
        <p>For an older Bridge or a computer without link support, issue a one-time code, open a new terminal, run <code>auteur-bridge pair</code>, and paste it only into the hidden pairing prompt.</p>
        <button type="button" onClick={() => void issueManualCode()} disabled={busy || !label.trim()}>Issue one-time pairing code</button>
        {manualGrant ? <div>
          <label htmlFor="manual-pair-code">One-time pairing code</label>
          <input id="manual-pair-code" type="password" readOnly autoComplete="off" value={manualGrant.token} />
          <button type="button" onClick={() => void copyManualCode()}>Copy pairing code</button>
          <p>Expires {new Date(manualGrant.expires_at).toLocaleString()}.</p>
        </div> : null}
        {manualNotice ? <p role="status">{manualNotice}</p> : null}
      </details>
      <p><a href="/devices">View connected computers</a></p>
    </section>
  );
}

function DevicesPage() {
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState('');
  const [capabilities, setCapabilities] = useState([]);
  const [capabilityError, setCapabilityError] = useState(null);

  // Signed-dispatch form state.
  const [approvalId, setApprovalId] = useState('');
  const [signedEnvelopeRef, setSignedEnvelopeRef] = useState('');
  const [intent, setIntent] = useState('');
  const [dispatchBusy, setDispatchBusy] = useState(false);
  const [dispatchResult, setDispatchResult] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const response = await fetchDevices();
      setDevices(response.devices || []);
    } catch (err) {
      setDevices(null);
      setError(err instanceof Error ? err.message : 'the device list could not be loaded');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const select = useCallback(async (deviceId) => {
    setSelected(deviceId);
    setCapabilities([]);
    setCapabilityError(null);
    if (!deviceId) return;
    try {
      const response = await fetchDeviceCapabilities(deviceId);
      setCapabilities(response.capabilities || []);
    } catch (err) {
      setCapabilityError(err instanceof Error ? err.message : 'capabilities could not be loaded');
    }
  }, []);

  const dispatch = useCallback(
    async (deviceId) => {
      setDispatchBusy(true);
      setDispatchResult(null);
      try {
        const response = await requestRemoteDispatch({
          deviceId,
          approvalId,
          signedEnvelopeRef,
          intent,
        });
        setDispatchResult({ ok: true, detail: JSON.stringify(response) });
      } catch (err) {
        setDispatchResult({ ok: false, detail: err instanceof Error ? err.message : 'dispatch failed' });
      } finally {
        setDispatchBusy(false);
      }
    },
    [approvalId, signedEnvelopeRef, intent],
  );

  if (devices === null && error) {
    return (
      <section>
        <h1>Devices</h1>
        <p className="error" role="alert">{error}</p>
        <button type="button" className="secondary" onClick={() => void load()}>Retry</button>
      </section>
    );
  }

  if (devices === null) {
    return (
      <section>
        <h1>Devices</h1>
        <p>Loading…</p>
      </section>
    );
  }

  return (
    <section>
      <h1>Devices</h1>
      <p><Link href="/devices/pair">Pair a computer</Link></p>
      {error ? <p className="error">{error}</p> : null}
      {devices.length === 0 ? <p>No paired devices were reported by the server.</p> : null}
      <table>
        <thead>
          <tr>
            <th>Device</th>
            <th>Status</th>
            <th>Connection</th>
            <th>Last seen</th>
            <th>Capabilities</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((device) => (
            <tr key={device.device_id}>
              <td>{device.label}</td>
              <td><span className="badge">{device.status}</span></td>
              <td><span className="badge">{device.connection_state || (device.online ? 'connected' : 'offline')}</span></td>
              <td>{device.last_seen_at || 'never'}</td>
              <td>
                <button
                  type="button"
                  onClick={() => select(selected === device.device_id ? '' : device.device_id)}
                >
                  {selected === device.device_id ? 'Hide' : 'Show'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selected ? (
        <div className="device-detail">
          <h2>Capabilities — device {selected}</h2>
          {capabilityError ? (
            <p className="error" role="alert">{capabilityError}</p>
          ) : capabilities.length === 0 ? (
            <p>The server reported no advertised capabilities for this device.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Capability</th>
                  <th>Group</th>
                  <th>Version</th>
                  <th>Health</th>
                  <th>Advertised at</th>
                </tr>
              </thead>
              <tbody>
                {capabilities.map((capability) => (
                  <tr key={capability.capability_id}>
                    <td>{capability.capability_id}</td>
                    <td>{capability.group}</td>
                    <td>{capability.version}</td>
                    {/* Server-reported health, rendered verbatim — never invented. */}
                    <td><span className={`badge badge-health-${capability.health}`}>{capability.health}</span></td>
                    <td>{capability.advertised_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="dispatch-form">
            <h3>Remote dispatch</h3>
            <p className="note">
              Remote execution requires BOTH an approved approval and an envelope already
              signed on the device that owns the key. The backend bridge verifies the
              signature and the approval and enforces this gate server-side regardless of
              what this form sends; this page never signs anything itself.
            </p>
            <label>
              Approved approval id
              <input
                type="text"
                value={approvalId}
                onChange={(e) => setApprovalId(e.target.value)}
              />
            </label>
            <label>
              Signed envelope reference
              <input
                type="text"
                value={signedEnvelopeRef}
                onChange={(e) => setSignedEnvelopeRef(e.target.value)}
              />
            </label>
            <label>
              Intent
              <input
                type="text"
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
              />
            </label>
            <button
              type="button"
              disabled={dispatchBusy || !approvalId.trim() || !signedEnvelopeRef.trim()}
              onClick={() => dispatch(selected)}
            >
              Request dispatch
            </button>
            {dispatchResult ? (
              <p className={dispatchResult.ok ? 'success' : 'error'}>{dispatchResult.detail}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default DevicesPage;
